import { useAppSelector, useAppDispatch } from "./store/hooks";
import { useEffect } from "react";
import { setToken } from "./store/slices/authSlice";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
} from "react-router-dom";

// Import all components
import SplashScreen from "./components/Splash/SplashScreen";
import Home from "./pages/guest/Home";
import GuestNavbar from "./components/navigations/GuestNavbar";
import AuthNavbar from "./components/navigations/AuthNavbar";
import Insurance from "./pages/guest/Insurance";
import LifeInsurance from "./pages/guest/LifeInsurance";
import GeneralInsurance from "./pages/guest/GeneralInsurance";
import TravelInsurance from "./pages/guest/TravelInsurance";
import TripSecureInsurance from "./pages/guest/TripSecureInsurance";
import InsuranceChoose from "./pages/guest/InsuranceChoose";
import About from "./pages/guest/About";
import Blog from "./pages/guest/Blog";
import BlogAll from "./pages/guest/BlogAll";
import BlogRead from "./pages/guest/BlogRead";
import HelpCenter from "./pages/guest/HelpCenter";
import Support from "./pages/guest/Support";
import SafetyInformation from "./pages/guest/SafetyInformation";
import ContactUs from "./pages/guest/ContactUs";
import MediaEventsOverview from "./pages/guest/MediaEventsOverview";
import HelpCenterMediaEvents from "./pages/guest/HelpCenterMediaEvents";
import ContactUsMediaEvents from "./pages/guest/ContactUsMediaEvents";
import PrivacyPolicy from "./pages/guest/PrivacyPolicy";
import TermsOfService from "./pages/guest/TermsOfService";
import BecomeAProvider from "./pages/guest/BecomeAProvider";
import BecomeAnAgent from "./pages/guest/BecomeAnAgent";
import MediaEvents from "./pages/guest/MediaEvents";
import ConferenceTradeMission from "./pages/guest/ConferenceTradeMission";
import EventsListing from "./pages/guest/EventsListing";
import EventDetails from "./pages/guest/EventDetails";
import EventRegistrationRouter from "./components/media_and_events/EventRegistrationRouter";
import ConferencePayment from "./pages/guest/ConferencePayment";
import RegistrationSuccess from "./pages/guest/RegistrationSuccess";
import MyRegistrations from "./pages/user/MyRegistrations";
import ConferenceRegistrations from "./pages/user/ConferenceRegistrations";
import ConferenceRegistrationDetails from "./pages/user/ConferenceRegistrationDetails";
import MyEventRegistrations from "./pages/user/MyEventRegistrations";
import RegistrationReceipt from "./pages/user/RegistrationReceipt";
import ConferencePayLaterSessionGuard from "./components/ConferencePayLaterSessionGuard";

// Event booking pages
import EventTicketSelection from "./pages/guest/EventTicketSelection";
import EventGuestDetails from "./pages/guest/EventGuestDetails";
import EventPayment from "./pages/guest/EventPayment";
import EventRegistrationSuccess from "./pages/guest/EventRegistrationSuccess";

// Auth components
import SignIn from "./pages/guest/auth/SignIn";
import SignInEmail from "./pages/guest/auth/SignInEmail";
import SignInPhone from "./pages/guest/auth/SignInPhone";
import ForgotPassword from "./pages/guest/auth/ForgotPassword";
import VerifyCode from "./pages/guest/auth/VerifyCode";
import ResetPassword from "./pages/guest/auth/ResetPassword";
import SignUp from "./pages/guest/auth/SignUp";
import VerifyAccount from "./pages/guest/auth/VerifyAccount";

// Payment callback pages
import PaymentSuccess from "./pages/payment/PaymentSuccess";
import PaymentFailed from "./pages/payment/PaymentFailed";

// Consumer components
import Checkout from "./pages/consumer/Checkout";
import TaxiTripForm from "./pages/consumer/TaxiTripForm";
import Policy from "./pages/consumer/Policy";
import ClaimsList from "./pages/consumer/ClaimsList";
import StartClaim from "./pages/consumer/StartClaim";
import NewClaimForm from "./pages/consumer/NewClaimForm";
import RelativeClaimForm from "./pages/consumer/RelativeClaimForm";
import Reward from "./pages/consumer/Reward";
import Settings from "./pages/consumer/Settings";
import MyQuoteRequests from "./pages/consumer/MyQuoteRequests";

// Toast configuration
const toastConfig = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: "colored",
};

function ProtectedRoute({
  children,
  requireAuth = false,
  requireVerified = false,
  preventAuth = false,
}) {
  const { isAuthenticated, isVerified } = useAppSelector((s) => s.auth);

  // Redirect to verify account if user is not verified
  if (isAuthenticated && !isVerified) {
    return <Navigate to="/verify-account" replace />;
  }

  // Redirect to signin if user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Redirect to verify account if user is not verified
  if (requireVerified && (!isAuthenticated || !isVerified)) {
    return <Navigate to="/verify-account" replace />;
  }

  // Prevent authenticated users from accessing auth pages
  if (preventAuth && isAuthenticated && isVerified) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function RouteWithNavbar({ children, showAuthNavbar = false, user = null }) {
  if (showAuthNavbar) {
    return (
      <>
        <AuthNavbar user={user} />
        {children}
      </>
    );
  }

  return (
    <>
      <GuestNavbar />
      {children}
    </>
  );
}

function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isVerified, user } = useAppSelector((s) => s.auth);
  const hasCompletedSplash = useAppSelector(
    (s) => s.onboarding?.hasCompletedSplash
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      dispatch(setToken(token));
    }
  }, [dispatch]);

  const shouldShowAuthNavbarForGuestRoutes = isAuthenticated && isVerified;

  return (
    <>
      <ToastContainer {...toastConfig} />
      <BrowserRouter>
        <ConferencePayLaterSessionGuard />
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute requireAuth={false} requireVerified={false}>
                <GuestRoot
                  hasCompletedSplash={hasCompletedSplash}
                  showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                  user={user}
                />
              </ProtectedRoute>
            }
          />

          <Route
            path="/home"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <Home />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/insurance/choose"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <InsuranceChoose />
              </RouteWithNavbar>
            }
          />
          <Route
            path="/insurance/:type?"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <Insurance />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/insurance/life"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <LifeInsurance />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/insurance/general"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <GeneralInsurance />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/insurance/travel"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <TravelInsurance />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/insurance/road-trip"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <TripSecureInsurance />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/about"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <About />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/blog"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <Blog />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/blog/all"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <BlogAll />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/blog/:id"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <BlogRead />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/help"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <HelpCenter />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/support"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <Support />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/help-center"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <HelpCenter />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/safety-information"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <SafetyInformation />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/contact-us"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <ContactUs />
              </RouteWithNavbar>
            }
          />

          {/* Media & Events Support Pages */}
          <Route
            path="/media-events-overview"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <MediaEventsOverview />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/help-center-media-events"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <HelpCenterMediaEvents />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/contact-us-media-events"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <ContactUsMediaEvents />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/about-us"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <About />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/privacy-policy"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <PrivacyPolicy />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/terms-of-service"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <TermsOfService />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/become-a-provider"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <BecomeAProvider />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/become-an-agent"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <BecomeAnAgent />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/media-events"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <MediaEvents />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/conference-trade-mission"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <ConferenceTradeMission />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/events"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <EventsListing />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/event-details/:slug"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <EventDetails />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/event-details/:slug/register"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <EventRegistrationRouter />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/event-details/:slug/payment"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <ConferencePayment />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/event-details/:slug/registration-success"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <RegistrationSuccess />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/my-registrations"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <MyRegistrations />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-quote-requests"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <MyQuoteRequests />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/registration-receipt/:slug"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <RegistrationReceipt />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/conference-registrations"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <ConferenceRegistrations />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/conference-registration/:id"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <ConferenceRegistrationDetails />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-event-registrations"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <MyEventRegistrations />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          {/* Event Booking Routes */}
          <Route
            path="/event-details/:slug/book"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <EventTicketSelection />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/event-details/:slug/guest-details"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <EventGuestDetails />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/event-details/:slug/event-payment"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <EventPayment />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/event-details/:slug/event-success"
            element={
              <RouteWithNavbar
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              >
                <EventRegistrationSuccess />
              </RouteWithNavbar>
            }
          />

          <Route
            path="/partner"
            element={
              <GuestRoot
                hasCompletedSplash={hasCompletedSplash}
                showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                user={user}
              />
            }
          />

          {/* Authentication routes - accessible to unauthenticated users only */}
          <Route
            path="/signin"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <SignIn />
              </ProtectedRoute>
            }
          />

          <Route
            path="/signin/email"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <SignInEmail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/signin/phone"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <SignInPhone />
              </ProtectedRoute>
            }
          />

          <Route
            path="/signup"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <SignUp />
              </ProtectedRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <ForgotPassword />
              </ProtectedRoute>
            }
          />

          <Route
            path="/forgot-password/verify"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <VerifyCode />
              </ProtectedRoute>
            }
          />

          <Route
            path="/forgot-password/reset"
            element={
              <ProtectedRoute
                requireAuth={false}
                requireVerified={false}
                preventAuth={true}
              >
                <ResetPassword />
              </ProtectedRoute>
            }
          />

          {/* Verification route - only accessible to authenticated but unverified users */}
          <Route path="/verify-account" element={<VerifyAccount />} />

          <Route
            path="/taxi/form"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <TaxiTripForm />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <Checkout />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-policies"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <Policy />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claim/list"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <ClaimsList />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claim/new"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <StartClaim />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claim/new/relative"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <RelativeClaimForm />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claim/new/form"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <NewClaimForm />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/rewards"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <Reward />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute requireAuth={true} requireVerified={true}>
                <RouteWithNavbar showAuthNavbar={true} user={user}>
                  <Settings />
                </RouteWithNavbar>
              </ProtectedRoute>
            }
          />

          {/* ── Payment gateway callback routes ── */}
          {/* These are the URLs registered with Paystack / MoMo gateway.
              They accept ?reference=xxx&type=insurance|event|conference
              and optionally read context from sessionStorage. */}
          <Route
            path="/payment/success"
            element={<PaymentSuccess />}
          />

          <Route
            path="/payment/failed"
            element={<PaymentFailed />}
          />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

function GuestRoot({ hasCompletedSplash, showAuthNavbar, user }) {
  const showSplash = !hasCompletedSplash;
  const location = useLocation();

  // Only show splash screen on the root path (/)
  if (showSplash && location.pathname === "/") {
    return <SplashScreen />;
  }

  // For partner route, show a different component or redirect
  if (location.pathname === "/partner") {
    return (
      <RouteWithNavbar showAuthNavbar={showAuthNavbar} user={user}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <h1 className="mb-4 text-3xl font-bold text-gray-900">
              Become a Partner
            </h1>
            <p className="mb-8 text-gray-600">
              Partner information and signup coming soon...
            </p>
            <Link to="/" className="text-emerald-600 hover:text-emerald-700">
              Back to Home
            </Link>
          </div>
        </div>
      </RouteWithNavbar>
    );
  }

  return (
    <RouteWithNavbar showAuthNavbar={showAuthNavbar} user={user}>
      <Home />
    </RouteWithNavbar>
  );
}

export default App;
